import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  ActionButton,
  Form,
  FormAlerts,
  FormField,
  GetApiErrorMessage,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  TokenValidFormDataSchema,
  TokenValidFormDataType,
  useMutateToken,
  useMutationErrors,
} from '@atlas-frontend/sdk';
import { Button, Flex, Result } from 'antd';
import { FC, useState } from 'react';
import translations from './AddTokenModal.translations.json';

const { addTranslations, t } = i18nNsFactory('add-token-modal');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t(`alert.error.code.${code}`, meta?.args && { ...meta.args });

export interface AddTokenModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export const AddTokenModal: FC<AddTokenModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { mutateAsync, isPending } = useMutateToken();
  const [createdName, setCreatedName] = useState<string | null>(null);
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const handleClose = () => {
    onSuccess?.();
    onClose();
  };

  if (createdName !== null) {
    return (
      <Modal onCancel={handleClose} width="30rem" footer={null}>
        <Result
          status="success"
          title={t('result.success.title')}
          subTitle={t('result.success.subtitle', { createdName })}
          extra={
            <Button
              id="add-token-success-ok"
              type="primary"
              onClick={handleClose}
            >
              {t('button.ok')}
            </Button>
          }
        />
      </Modal>
    );
  }

  return (
    <Form
      schema={TokenValidFormDataSchema}
      onSubmit={(data: TokenValidFormDataType) => {
        clearMutationErrors();
        return mutateAsync({ data })
          .then(async (created) => {
            if (created) {
              await navigator.clipboard.writeText(created.token);
            }
            setCreatedName(created?.name ?? '');
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }}
      serverErrors={mutationErrors}
    >
      <AddTokenModalInner isLoading={isPending} onClose={onClose} />
    </Form>
  );
};

const AddTokenModalInner: FC<{
  isLoading?: boolean;
  onClose: () => void;
}> = ({ isLoading, onClose }) => (
  <Modal
    onCancel={onClose}
    width="30rem"
    footer={
      <ModalFooter>
        <Flex gap="small" justify="end">
          <ActionButton type="abort" onClick={onClose} />
          <ActionButton
            type="formSubmit"
            label={t('button.create')}
            loading={isLoading}
          />
        </Flex>
      </ModalFooter>
    }
  >
    <ModalHeader>
      <ModalTitle>
        <TextEllipsis>{t('title')}</TextEllipsis>
      </ModalTitle>
    </ModalHeader>
    <FormAlerts />
    <Padding>
      <FormField name="name" label={t('field.name.label')} required />
    </Padding>
  </Modal>
);
